import { tagPresets, defaultTagConfig } from "../config/tagPresets";

export const SECOND_QR_FIXED_VALUE = "#anm2026";

/** Defaults para campos novos que podem não existir em configs salvas anteriormente */
const NEW_FIELD_DEFAULTS = {
    qrSide: "left",
    textAreaLeft: 24,
    textAreaTop: 14,
    textAreaSpacing: 1,
    itemOrder: ["role", "name", "company"],
    nameUppercase: true,
    nameItalic: false,
    companyUppercase: false,
    companyItalic: false,
    roleUppercase: false,
    roleItalic: false,
    showSecondQr: false,
    secondQrSide: "right",
    secondQrCodePosition: { top: 14, left: 2 },
    secondQrCodeSizeMm: 20,
    secondQrValue: SECOND_QR_FIXED_VALUE,
};

/**
 * Garante que tamanho e posição de um QR Code respeitem os limites físicos da etiqueta (widthMm x heightMm).
 */
export const clampQrBounds = (widthMm, heightMm, sizeMm, position = { top: 0, left: 0 }, side = "left") => {
    const w = Math.max(10, Number(widthMm) || 90);
    const h = Math.max(10, Number(heightMm) || 50);
    const maxAllowedSize = Math.min(w, h);
    const safeSize = Math.max(5, Math.min(Number(sizeMm) || 20, maxAllowedSize));

    const rawTop = Number(position?.top ?? 0);
    const rawOffset = Math.max(0, Number(position?.left ?? 0));

    const safeTop = Math.max(0, Math.min(rawTop, h - safeSize));
    const computedLeft = side === "right" ? w - safeSize - rawOffset : rawOffset;
    const safeLeft = Math.max(0, Math.min(computedLeft, w - safeSize));
    const safeOffset = side === "right" ? Math.max(0, w - safeSize - safeLeft) : safeLeft;

    return {
        sizeMm: safeSize,
        topMm: safeTop,
        leftMm: safeLeft,
        offsetMm: safeOffset,
    };
};

/**
 * Retorna a configuração de etiqueta ativa para o evento especificado.
 * Prioridade:
 * 1. Configuração salva no LocalStorage (Ajustes específicos locais)
 * 2. Preset pré-definido por ID de Evento em `tagPresets.js`
 * 3. Configuração padrão global (Zebra 9x5cm)
 * 
 * @param {string|number} eventId ID do evento
 * @returns {object} Configuração de etiqueta ativa
 */
export const getTagConfig = (eventId) => {
    const id = String(eventId);
    
    // 1. Tentar ler do LocalStorage
    try {
        const localSaved = localStorage.getItem(`tag_config_${id}`);
        if (localSaved) {
            const parsed = JSON.parse(localSaved);
            if (validateTagConfig(parsed)) {
                // Merge com defaults para campos novos não presentes em configs antigas
                return {
                    ...NEW_FIELD_DEFAULTS,
                    ...parsed,
                    secondQrValue: SECOND_QR_FIXED_VALUE,
                };
            }
        }
    } catch (error) {
        console.error("Erro ao ler configuração de tag do LocalStorage:", error);
    }
    
    // 2. Tentar buscar preset pré-cadastrado no código para este evento específico
    if (tagPresets.events && tagPresets.events[id]) {
        return {
            ...NEW_FIELD_DEFAULTS,
            ...tagPresets.events[id],
            secondQrValue: SECOND_QR_FIXED_VALUE,
        };
    }
    
    // 3. Fallback para a configuração padrão global
    return {
        ...NEW_FIELD_DEFAULTS,
        ...defaultTagConfig,
        secondQrValue: SECOND_QR_FIXED_VALUE,
    };
};

/**
 * Salva a configuração de etiqueta no LocalStorage do navegador para o evento atual.
 * 
 * @param {string|number} eventId ID do evento
 * @param {object} config Configuração de etiqueta a ser salva
 * @returns {boolean} Retorna true se salvou com sucesso
 */
export const saveTagConfig = (eventId, config) => {
    const id = String(eventId);
    if (!validateTagConfig(config)) {
        console.error("Configuração de etiqueta inválida para salvar:", config);
        return false;
    }
    
    try {
        localStorage.setItem(`tag_config_${id}`, JSON.stringify(config));
        return true;
    } catch (error) {
        console.error("Erro ao salvar configuração de tag no LocalStorage:", error);
        return false;
    }
};

/**
 * Valida os campos básicos de uma configuração de etiqueta.
 * 
 * @param {object} config Objeto de configuração
 * @returns {boolean}
 */
export const validateTagConfig = (config) => {
    if (!config || typeof config !== "object") return false;
    
    const requiredFields = ["widthMm", "heightMm"];
    const hasRequired = requiredFields.every(field => typeof config[field] === "number" && config[field] > 0);
    
    return hasRequired;
};

/**
 * Exporta a configuração para uma string JSON formatada.
 * 
 * @param {object} config 
 * @returns {string}
 */
export const exportTagConfig = (config) => {
    try {
        return JSON.stringify(config, null, 2);
    } catch (error) {
        console.error("Erro ao exportar configuração de tag:", error);
        return "";
    }
};

/**
 * Importa uma configuração a partir de uma string JSON, validando sua estrutura.
 * 
 * @param {string} jsonString 
 * @returns {object|null} Retorna o objeto se for válido, senão null
 */
export const importTagConfig = (jsonString) => {
    try {
        const parsed = JSON.parse(jsonString);
        
        // Garantir que tipos numéricos estejam corretos após importação
        const normalized = {
            ...NEW_FIELD_DEFAULTS,
            ...parsed,
            widthMm: Number(parsed.widthMm),
            heightMm: Number(parsed.heightMm),
            qrCodeSizeMm: Number(parsed.qrCodeSizeMm ?? 25),
            nameFontSize: Number(parsed.nameFontSize ?? 16),
            companyFontSize: Number(parsed.companyFontSize ?? 12),
            roleFontSize: Number(parsed.roleFontSize ?? 10),
            textAreaLeft: Number(parsed.textAreaLeft ?? 40),
            textAreaTop: Number(parsed.textAreaTop ?? 10),
            textAreaSpacing: Number(parsed.textAreaSpacing ?? 7),
            qrSide: parsed.qrSide || "left",
            itemOrder: Array.isArray(parsed.itemOrder) ? parsed.itemOrder : ["name", "company", "role"],
            qrCodePosition: {
                top: Number(parsed.qrCodePosition?.top ?? 0),
                left: Number(parsed.qrCodePosition?.left ?? 0),
            },
            showSecondQr: Boolean(parsed.showSecondQr),
            secondQrSide: parsed.secondQrSide || "right",
            secondQrCodeSizeMm: Number(parsed.secondQrCodeSizeMm ?? 20),
            secondQrCodePosition: {
                top: Number(parsed.secondQrCodePosition?.top ?? 14),
                left: Number(parsed.secondQrCodePosition?.left ?? 2),
            },
            secondQrValue: SECOND_QR_FIXED_VALUE,
        };
        
        if (validateTagConfig(normalized)) {
            return normalized;
        }
    } catch (error) {
        console.error("Erro ao analisar JSON para importar configuração:", error);
    }
    return null;
};
